import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Eye } from 'lucide-react';
import { useObservationPending } from '@/api/practicalObservations';
import { ObservationCards } from './_components/observation-cards';

export function ObservationsListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const perPage = 20;
  const { data: response, isLoading, error } = useObservationPending(page, perPage);
  const items = response?.data ?? [];
  const total = response?.meta?.pagination?.total ?? 0;
  const totalPages = response?.meta?.pagination?.total_pages ?? 1;

  return (
    <div className="p-6">
      <div className="mb-4 flex items-baseline justify-between">
        <h1 className="text-2xl font-bold text-foreground">Observaciones pendientes</h1>
        <span className="text-sm text-muted-foreground">{total} solicitudes</span>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}

      {error && (
        <p className="text-sm text-rose-600">
          Error al cargar: {error instanceof Error ? error.message : 'Desconocido'}
        </p>
      )}

      {!isLoading && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No hay solicitudes de observación pendientes.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="hidden overflow-hidden rounded-lg border border-border bg-card md:block">
            <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Colaborador</th>
                  <th className="px-3 py-2 font-medium">Puesto</th>
                  <th className="px-3 py-2 font-medium">Curso</th>
                  <th className="px-3 py-2 font-medium">Lección</th>
                  <th className="px-3 py-2 font-medium">Criterios</th>
                  <th className="px-3 py-2 font-medium">Solicitada</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((r) => (
                  <tr key={r.id} className="hover:bg-accent/30">
                    <td className="px-3 py-2 font-medium">
                      {r.enrollment.user.first_name} {r.enrollment.user.last_name}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {r.enrollment.position?.name ?? '—'}
                    </td>
                    <td className="px-3 py-2">{r.enrollment.course.title}</td>
                    <td className="px-3 py-2">{r.lesson.title}</td>
                    <td className="px-3 py-2 text-center">
                      {r.lesson.observation_criteria.length}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {new Date(r.requested_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/instructor/observations/${r.id}`}
                        className="inline-flex items-center gap-1 rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                      >
                        <Eye className="h-3 w-3" /> Confirmar
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ObservationCards
            items={items}
            onConfirm={(id) => navigate(`/instructor/observations/${id}`)}
          />
        </>
      )}

      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border border-border px-3 py-1 disabled:opacity-50"
          >
            Anterior
          </button>
          <span className="text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border border-border px-3 py-1 disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
}
